import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import { Link, useNavigate, useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import StageChangeModal from '../components/StageChangeModal';
import BookAppointmentModal from '../components/BookAppointmentModal';
import RescheduleModal from '../components/RescheduleModal';
import { useCrm, isClosedStage } from '../lib/crm';
import { useToast } from '../lib/toast';
import { apiErrorMessage } from '../lib/apiError';
import { useVehicleMap } from '../lib/useVehicleMap';
import { useConsultantOptions } from '../lib/useDirectory';
import DealQuotesPanel from '../components/DealQuotesPanel';
import { nextBestAction, actionColor } from '../lib/nextAction';
import {
  DEAL_COLUMNS, isDealStage, toDeal, fmtMoney, dealStageLabel, probabilityOf,
} from '../lib/deals';
import {
  appointmentsService, financeService, leadsService,
} from '../lib/services';

/**
 * Deal detail — the commercial view of an opportunity.
 *
 * Deliberately NOT a copy of Lead Detail. Lead Detail is about the
 * conversation (who is this person, what are they saying, what does the AI
 * reply). This page is about the money: what are we selling, for how much,
 * what's it worth weighted, what is the customer trading in, what will the
 * repayments look like, and what has to happen for it to close.
 *
 * The conversation lives one click away on the lead.
 */

const FORWARD = ['NEGOTIATION', 'CONFIRMED', 'DELIVERY_BOOKED', 'RETAILED', 'DELIVERED'];

const fmtDate = (iso) => formatInOrgTz(iso, { day: '2-digit', month: 'short', year: 'numeric' });

const fmtDateTime = (iso) => formatInOrgTz(iso, {
  weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
});

const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { leads, getCustomer, moveDeal, refreshLeads } = useCrm();
  const { toast } = useToast();
  const { vehiclesById } = useVehicleMap();
  // Name of the consultant handling this deal (shown in the Customer card).
  const { options: consultantOptions } = useConsultantOptions();

  const [appointments, setAppointments] = useState([]);
  const [stageOpen, setStageOpen] = useState(false);
  // When the user clicks "Mark lost…" we pre-select LOST in the modal;
  // "Change stage" opens it with no pre-selection.
  const [stageIntent, setStageIntent] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [rescheduleFor, setRescheduleFor] = useState(null);

  const openStageModal = (intent = null) => {
    setStageIntent(intent);
    setStageOpen(true);
  };

  // Finance calculator inputs.
  const [deposit, setDeposit]       = useState(0);
  const [termYears, setTermYears]   = useState(5);
  const [balloonPct, setBalloonPct] = useState(0);
  const [quote, setQuote]           = useState(null);
  const [quoting, setQuoting]       = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [dealQuotes, setDealQuotes] = useState({ quotes: [], sale: null });

  const lead = leads.find(l => l.id === id);
  const customer = getCustomer(lead?.customerId);
  const vehicle = lead?.vehicleOfInterestId ? vehiclesById.get(lead.vehicleOfInterestId) : null;

  const deal = useMemo(
    () => (lead ? toDeal(lead, customer, vehicle) : null),
    [lead, customer, vehicle],
  );

  const refreshAppointments = useCallback(async () => {
    if (!id) return;
    try {
      const page = await appointmentsService.list({ leadId: id, page: 0, size: 50 });
      setAppointments(page?.content ?? (Array.isArray(page) ? page : []));
    } catch (_) { /* keep previous */ }
  }, [id]);

  useEffect(() => { refreshAppointments(); }, [refreshAppointments]);

  const tradeInValue = 0; // no valuation figure is persisted yet — see trade-in card

  // Recompute the indicative finance quote whenever the inputs move.
  useEffect(() => {
    if (!deal?.value) return;
    let cancelled = false;
    setQuoting(true);
    setQuoteError(null);

    financeService.indicativeQuote({
      vehicleId:         deal.vehicleOfInterestId || undefined,
      driveAwayPriceAud: deal.value,
      termYears,
      depositAud:        Number(deposit) || 0,
      tradeInValueAud:   tradeInValue,
      balloonPct:        Number(balloonPct) || 0,
      financeType:       'CONSUMER_LOAN',
    })
      .then((res) => {
        if (cancelled) return;
        setQuote(res?.data ?? res);
      })
      .catch((err) => {
        if (!cancelled) setQuoteError(apiErrorMessage(err, 'Quote unavailable'));
      })
      .finally(() => { if (!cancelled) setQuoting(false); });

    return () => { cancelled = true; };
  }, [deal?.value, deal?.vehicleOfInterestId, deposit, termYears, balloonPct]);

  if (!lead || !deal) {
    return (
      <>
        <TopBar crumbs={[['Deals', '/deals'], 'Not found']} />
        <div className="app-content">
          <div className="empty-state">
            <div className="icon">?</div>
            <div className="title">Deal not found</div>
            <button className="btn primary small" style={{ marginTop: 14 }} onClick={() => navigate('/deals')}>
              ← Back to deals
            </button>
          </div>
        </div>
      </>
    );
  }

  // A lead that hasn't reached a commercial stage isn't a deal yet.
  if (!isDealStage(deal.stage)) {
    return (
      <>
        <TopBar crumbs={[['Deals', '/deals'], customer?.name || 'Not a deal yet']} />
        <div className="app-content">
          <div className="empty-state">
            <div className="icon">◔</div>
            <div className="title">Not a deal yet</div>
            <div className="desc" style={{ maxWidth: 420 }}>
              {customer?.name || 'This lead'} is still at <strong>{dealStageLabel(deal.stage)}</strong>.
              A lead becomes a deal once it reaches Quoting — there's no money on the
              table before a price is on the table.
            </div>
            <Link to={`/leads/${id}`} className="btn primary small" style={{ marginTop: 14 }}>
              Open the lead →
            </Link>
          </div>
        </div>
      </>
    );
  }

  const col       = DEAL_COLUMNS.find(c => c.key === deal.stage) || {};
  const closed    = isClosedStage(deal.stage);
  const stageIdx  = FORWARD.indexOf(deal.stage);
  const nextStage = (!closed && stageIdx >= 0 && stageIdx < FORWARD.length - 1)
    ? FORWARD[stageIdx + 1]
    : null;

  const ageDays        = daysSince(deal.createdAt);
  const daysInStage    = daysSince(deal.stageEnteredAt);
  const deliveryAppt   = appointments.find(a => a.type === 'DELIVERY' && a.status !== 'CANCELLED');
  const openAppts      = appointments
    .filter(a => !['CANCELLED', 'COMPLETED'].includes(a.status))
    .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));

  const onAdvance = async (to) => {
    try {
      await moveDeal(id, to, {});
      toast(`Deal → ${dealStageLabel(to)}`, {
        variant: to === 'DELIVERED' ? 'success' : 'info',
      });
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not move the deal'), { variant: 'danger' });
    }
  };

  const onReopen = async () => {
    try {
      await leadsService.reopen(id, 'NEGOTIATION');
      await refreshLeads?.();
      toast('Deal reopened at Negotiating', { variant: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not reopen the deal'), { variant: 'danger' });
    }
  };

  /* ─── Deal Desk bridge + next-best-action ─── */
  const newQuote = () => {
    const p = new URLSearchParams({ leadId: id });
    if (deal.customerId) p.set('customerId', deal.customerId);
    if (deal.vehicleOfInterestId) p.set('vehicleId', deal.vehicleOfInterestId);
    navigate(`/quotes/new?${p.toString()}`);
  };
  const runAction = (key) => {
    if (key === 'quote') newQuote();
    else if (key === 'book' || key === 'deliver') setBookOpen(true);
    else navigate(`/leads/${id}`);   // reply / follow_up / assign → conversation & assignment
  };
  const runChecklist = (action) => {
    if (action === 'quote') newQuote();
    else if (action === 'book') setBookOpen(true);
    else if (action === 'stage') openStageModal();
    else if (action === 'deliver') onAdvance('DELIVERED');
    else navigate(`/leads/${id}`);   // 'lead' — vehicle / trade-in live on the conversation
  };
  const hero = nextBestAction({
    stage: deal.stage,
    unread: false,
    hasConsultant: !!deal.assignedConsultantId,
    quotes: dealQuotes.quotes,
    sale: dealQuotes.sale,
    hasOpenAppt: openAppts.length > 0,
  });

  /* ─── margin / pricing ─── */
  const rrp = vehicle?.rrpCents ? Math.round(vehicle.rrpCents / 100) : null;
  const discount = rrp && rrp > deal.value ? rrp - deal.value : null;

  return (
    <>
      <TopBar
        crumbs={[['Deals', '/deals'], customer?.name || 'Deal']}
        right={
          <>
            <span className="pill filled" style={{ background: 'var(--bg-glass)', color: col.color }}>
              {dealStageLabel(deal.stage)}
            </span>
            <Link to={`/leads/${id}`} className="btn small" title="The conversation lives on the lead">
              💬 Conversation
            </Link>
            {nextStage && (
              <button className="btn small primary" onClick={() => onAdvance(nextStage)}>
                → {dealStageLabel(nextStage)}
              </button>
            )}
            {!closed && (
              <button className="btn small" onClick={() => openStageModal()}>Change stage</button>
            )}
            {closed && (
              <button className="btn small" onClick={onReopen}>↺ Reopen</button>
            )}
          </>
        }
      />

      <div className="app-content" style={{ overflow: 'auto' }}>

        {/* ── Headline: what is this deal worth ── */}
        <div className="grid-4" style={{ marginBottom: 14 }}>
          <div className="card">
            <div className="card-label">DEAL VALUE</div>
            <div className="metric-value" style={{ fontSize: 28, color: 'var(--success)' }}>
              {deal.valueIsEstimated ? '~' : ''}{fmtMoney(deal.value)}
            </div>
            <div className="mono text-xs text-muted">
              {deal.valueIsEstimated
                ? 'estimated — no driveaway price linked'
                : 'driveaway price'}
            </div>
          </div>

          <div className="card">
            <div className="card-label">WEIGHTED</div>
            <div className="metric-value" style={{ fontSize: 28, color: 'var(--info)' }}>
              {fmtMoney(deal.weighted)}
            </div>
            <div className="mono text-xs text-muted">{deal.probability}% close probability</div>
          </div>

          <div className="card">
            <div className="card-label">AGE</div>
            <div className="metric-value" style={{ fontSize: 28 }}>
              {ageDays == null ? '—' : `${ageDays}d`}
            </div>
            <div className="mono text-xs text-muted">
              {daysInStage == null ? '—' : `${daysInStage}d in ${dealStageLabel(deal.stage).toLowerCase()}`}
            </div>
          </div>

          <div className="card">
            <div className="card-label">LEAD SCORE</div>
            <div
              className="metric-value"
              style={{ fontSize: 28, color: deal.score >= 80 ? 'var(--success)' : deal.score <= 40 ? 'var(--danger)' : 'var(--warning)' }}
            >
              {deal.score}
            </div>
            <div className="mono text-xs text-muted">buyer intent signal</div>
          </div>
        </div>

        {/* ── Pipeline progress ── */}
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-label">DEAL PROGRESS</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {FORWARD.map((s, i) => {
              const done    = stageIdx >= 0 && i <= stageIdx;
              const isNow   = s === deal.stage;
              const scol    = DEAL_COLUMNS.find(c => c.key === s);
              return (
                <div key={s} style={{ flex: '1 1 120px', minWidth: 120 }}>
                  <div
                    style={{
                      height: 5, borderRadius: 3, marginBottom: 6,
                      background: deal.stage === 'LOST'
                        ? 'var(--border-color)'
                        : done ? scol.color : 'var(--border-color)',
                    }}
                  />
                  <div
                    className="mono text-xs"
                    style={{
                      color: isNow ? scol.color : 'var(--text-muted)',
                      fontWeight: isNow ? 700 : 400,
                    }}
                  >
                    {scol.label}
                    <span className="text-muted"> · {probabilityOf(s)}%</span>
                  </div>
                </div>
              );
            })}
          </div>

          {deal.stage === 'LOST' && (
            <div className="mono text-xs" style={{ marginTop: 10, color: 'var(--danger)', lineHeight: 1.7 }}>
              LOST — {deal.lostReasonCode || 'no reason recorded'}
              {deal.lostNotes && <><br /><span className="text-muted">{deal.lostNotes}</span></>}
              {deal.closedAt && <><br /><span className="text-muted">Closed {fmtDate(deal.closedAt)}</span></>}
            </div>
          )}

          {deal.stage === 'DELIVERED' && (
            <div className="mono text-xs" style={{ marginTop: 10, color: 'var(--success)' }}>
              WON — delivered {fmtDate(deal.closedAt)}
            </div>
          )}
        </div>

        {/* ── Next best action ── */}
        {hero && (
          <div
            className="card"
            style={{
              marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, borderLeft: `4px solid ${actionColor(hero.tone)}`,
            }}
          >
            <div>
              <div className="card-label" style={{ color: actionColor(hero.tone) }}>NEXT BEST ACTION</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{hero.label}</div>
              <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{hero.hint}</div>
            </div>
            <button
              className="btn primary"
              style={{ background: actionColor(hero.tone), border: 'none', whiteSpace: 'nowrap' }}
              onClick={() => runAction(hero.key)}
            >
              {hero.label} →
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* ── Left column ── */}
          <div style={{ flex: '1 1 380px', minWidth: 340, display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Vehicle being sold */}
            <div className="card">
              <div className="flex-between">
                <span className="card-label">VEHICLE</span>
                {vehicle?.stockStatus && (
                  <span className={`pill ${vehicle.stockStatus === 'AVAILABLE' ? 'success' : 'warning'} filled`}>
                    {vehicle.stockStatus}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                {vehicle?.primaryPhotoUrl && (
                  <img
                    src={vehicle.primaryPhotoUrl}
                    alt={deal.title}
                    style={{ width: 108, height: 72, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{deal.title}</div>
                  {vehicle ? (
                    <div className="mono text-xs text-muted" style={{ marginTop: 5, lineHeight: 1.8 }}>
                      {[vehicle.body, vehicle.transmission, vehicle.fuel].filter(Boolean).join(' · ') || '—'}
                      <br />
                      {vehicle.km != null ? `${vehicle.km.toLocaleString()} km` : '— km'}
                      {vehicle.condition && ` · ${vehicle.condition}`}
                      {vehicle.vin && <><br />VIN {vehicle.vin}</>}
                      {/* `dmsStockId` is the backend field name — the label the
                          user sees is just "Stock". */}
                      {vehicle.dmsStockId && <><br />Stock {vehicle.dmsStockId}</>}
                    </div>
                  ) : (
                    <div className="mono text-xs text-muted" style={{ marginTop: 5, lineHeight: 1.7 }}>
                      No stock vehicle linked to this deal.
                      <br />
                      <Link to={`/leads/${id}`} style={{ color: 'var(--accent-primary-hover)' }}>
                        Link one on the lead →
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing */}
              {(rrp || !deal.valueIsEstimated) && (
                <div style={{ marginTop: 12, borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
                  <Row k="Driveaway" v={fmtMoney(deal.value)} strong />
                  {rrp && <Row k="RRP" v={fmtMoney(rrp)} />}
                  {discount && (
                    <Row k="Discount" v={`−${fmtMoney(discount)}`} color="var(--warning)" />
                  )}
                </div>
              )}
            </div>

            {/* Trade-in */}
            <div className="card">
              <div className="card-label">TRADE-IN</div>
              {deal.tradeIn ? (
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {[deal.tradeIn.year, deal.tradeIn.make, deal.tradeIn.model].filter(Boolean).join(' ') || 'Vehicle'}
                  </div>
                  <div className="mono text-xs text-muted" style={{ marginTop: 6, lineHeight: 1.8 }}>
                    {deal.tradeIn.rego && <>Rego {deal.tradeIn.rego}<br /></>}
                    {deal.tradeIn.km != null && <>{Number(deal.tradeIn.km).toLocaleString()} km<br /></>}
                    {deal.tradeIn.condition && <>Condition: {deal.tradeIn.condition}<br /></>}
                    {deal.tradeIn.captured_at && <>Captured {fmtDate(deal.tradeIn.captured_at)}</>}
                  </div>
                  {deal.tradeIn.notes && (
                    <div className="text-xs" style={{ marginTop: 6, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      {deal.tradeIn.notes}
                    </div>
                  )}
                  <div className="mono text-xs" style={{ marginTop: 8, color: 'var(--warning)' }}>
                    ⓘ Awaiting valuation — no figure is applied to the deal or the finance quote yet.
                  </div>
                </div>
              ) : (
                <div className="mono text-xs text-muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
                  No trade-in captured. If the customer has one, capturing it usually
                  moves the deal — it's the most common reason a quote stalls.
                </div>
              )}
            </div>

            {/* Customer */}
            <div className="card">
              <div className="card-label">CUSTOMER</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <div className="avatar-pill" style={{ width: 40, height: 40, fontSize: 13 }}>
                  {customer?.initials || '··'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{customer?.name || '—'}</div>
                  <div className="mono text-xs text-muted">
                    {customer?.phone || '—'} · {customer?.email || '—'}
                  </div>
                </div>
              </div>
              <div className="mono text-xs text-muted" style={{ marginTop: 8, lineHeight: 1.8 }}>
                Source: {deal.source}<br />
                <span style={{ color: deal.assignedConsultantId ? 'var(--text-primary)' : 'var(--warning)', fontWeight: 600 }}>
                  Consultant:{' '}
                  {deal.assignedConsultantId
                    ? (consultantOptions.find(o => o.id === deal.assignedConsultantId)?.primary
                        || `Consultant ${String(deal.assignedConsultantId).slice(0, 8)}`)
                    : 'Unassigned'}
                </span><br />
                {customer?.ltv > 0 && <>Lifetime value: {fmtMoney(customer.ltv)}<br /></>}
                Deal opened: {fmtDate(deal.createdAt)}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {customer && (
                  <Link to={`/customers/${customer.id}`} className="btn small">Customer 360 →</Link>
                )}
                <Link to={`/leads/${id}`} className="btn small">Conversation →</Link>
              </div>
            </div>
          </div>

          {/* ── Right column ── */}
          <div style={{ flex: '1 1 380px', minWidth: 340, display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Quotes & sale — the bridge into the Deal Desk */}
            <DealQuotesPanel
              leadId={id}
              customerId={deal.customerId}
              vehicleId={deal.vehicleOfInterestId}
              onData={setDealQuotes}
            />

            {/* Finance calculator */}
            <div className="card">
              <div className="flex-between">
                <span className="card-label">INDICATIVE FINANCE</span>
                {quoting && <span className="mono text-xs text-muted">calculating…</span>}
              </div>
              <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
                Quick estimate. <button type="button" onClick={newQuote} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-primary-hover)' }}>Build a full quote →</button>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                <div className="form-field" style={{ flex: '1 1 100px' }}>
                  <label htmlFor="dd-deposit">Deposit</label>
                  <input
                    id="dd-deposit"
                    type="number"
                    min={0}
                    step={500}
                    value={deposit}
                    onChange={(e) => setDeposit(e.target.value)}
                  />
                </div>
                <div className="form-field" style={{ flex: '1 1 90px' }}>
                  <label htmlFor="dd-term">Term (yrs)</label>
                  <select id="dd-term" value={termYears} onChange={(e) => setTermYears(Number(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6, 7].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-field" style={{ flex: '1 1 90px' }}>
                  <label htmlFor="dd-balloon">Balloon %</label>
                  <input
                    id="dd-balloon"
                    type="number"
                    min={0}
                    max={60}
                    step={5}
                    value={balloonPct}
                    onChange={(e) => setBalloonPct(e.target.value)}
                  />
                </div>
              </div>

              {quoteError && (
                <div className="text-xs" style={{ color: 'var(--danger)', marginTop: 8 }}>{quoteError}</div>
              )}

              {quote && !quoteError && (
                <div style={{ marginTop: 12, borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'baseline' }}>
                    <div>
                      <div className="metric-value" style={{ fontSize: 26, color: 'var(--accent-primary)' }}>
                        ${Math.round(quote.weekly_aud)}
                      </div>
                      <div className="mono text-xs text-muted">per week</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>
                        ${Math.round(quote.monthly_aud).toLocaleString()}
                      </div>
                      <div className="mono text-xs text-muted">per month</div>
                    </div>
                  </div>

                  <div className="mono text-xs text-muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
                    Rate {quote.advertised_rate_pct}% · comparison {quote.comparison_rate_pct}%<br />
                    Term {quote.term_years}y · total ${Math.round(quote.total_aud).toLocaleString()}
                    {quote.balloon_aud > 0 && <> · balloon ${Math.round(quote.balloon_aud).toLocaleString()}</>}
                  </div>

                  {/* Always visible, directly under the figures — not behind a toggle. */}
                  <div className="text-xs text-muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
                    {(quote.disclosures || []).map((d, i) => <p key={i} style={{ margin: '0 0 6px' }}>{d}</p>)}
                  </div>
                </div>
              )}
            </div>

            {/* Appointments — the things that actually close a deal */}
            <div className="card">
              <div className="flex-between" style={{ marginBottom: 6 }}>
                <span className="card-label">APPOINTMENTS</span>
                <button className="btn small primary" onClick={() => setBookOpen(true)}>+ Book</button>
              </div>

              {deliveryAppt && (
                <div
                  className="mono text-xs"
                  style={{ color: 'var(--success)', marginBottom: 8, lineHeight: 1.6 }}
                >
                  🚗 Delivery {fmtDateTime(deliveryAppt.startsAt)} · {deliveryAppt.status}
                </div>
              )}

              {openAppts.length === 0 ? (
                <div className="mono text-xs text-muted" style={{ lineHeight: 1.6 }}>
                  Nothing booked. A test drive or a delivery slot is usually what turns a
                  quote into a signature.
                </div>
              ) : openAppts.map(a => (
                <div key={a.id} style={{ padding: '8px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <div className="flex-between">
                    <span className="mono text-xs" style={{ fontWeight: 600 }}>
                      {String(a.type || '').replace(/_/g, ' ')}
                    </span>
                    <span className={`pill ${a.status === 'CONFIRMED' ? 'success' : 'warning'} filled`}>
                      {a.status}
                    </span>
                  </div>
                  <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
                    {fmtDateTime(a.startsAt)}
                  </div>
                  <button
                    className="btn small"
                    style={{ marginTop: 6 }}
                    onClick={() => setRescheduleFor(a)}
                  >
                    📅 Reschedule
                  </button>
                </div>
              ))}
            </div>

            {/* Close plan */}
            <div className="card">
              <div className="card-label">CLOSE PLAN</div>
              <div className="mono text-xs text-muted" style={{ marginTop: 6, lineHeight: 1.8 }}>
                {buildCloseChecklist(deal, deliveryAppt).map(({ done, text, action, cta }, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ color: done ? 'var(--success)' : 'var(--text-tertiary)' }}>
                      {done ? '✓' : '○'}
                    </span>
                    <span style={{ color: done ? 'var(--text-secondary)' : 'var(--text-muted)', flex: 1 }}>{text}</span>
                    {!done && !closed && action && (
                      <button type="button" className="btn small" style={{ padding: '1px 8px', fontSize: 11 }}
                        onClick={() => runChecklist(action)}>{cta} →</button>
                    )}
                  </div>
                ))}
              </div>

              {!closed && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                  {nextStage && (
                    <button className="btn small primary" onClick={() => onAdvance(nextStage)}>
                      → Move to {dealStageLabel(nextStage)}
                    </button>
                  )}
                  <button className="btn small danger" onClick={() => openStageModal('LOST')}>
                    Mark lost…
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <StageChangeModal
        open={stageOpen}
        lead={deal}
        initialTarget={stageIntent}
        onClose={() => { setStageOpen(false); setStageIntent(null); }}
        onChange={async (to, opts) => {
          await moveDeal(id, to, opts);
          toast(`Deal → ${dealStageLabel(to)}`, { variant: to === 'LOST' ? 'warning' : 'success' });
        }}
      />

      <BookAppointmentModal
        open={bookOpen}
        lead={lead}
        customer={customer}
        onClose={() => setBookOpen(false)}
        onBooked={() => {
          refreshAppointments();
          toast('Appointment booked', { variant: 'success' });
        }}
      />

      <RescheduleModal
        open={!!rescheduleFor}
        appointment={rescheduleFor}
        onClose={() => setRescheduleFor(null)}
        onRescheduled={() => {
          refreshAppointments();
          toast('Appointment rescheduled', { variant: 'success' });
        }}
      />
    </>
  );
}

/** What still has to be true for this deal to close. */
function buildCloseChecklist(deal, deliveryAppt) {
  return [
    { done: !!deal.vehicleOfInterestId, text: 'Vehicle selected from stock', action: 'lead',  cta: 'Choose' },
    { done: !deal.valueIsEstimated,     text: 'Driveaway price agreed',      action: 'quote', cta: 'Quote' },
    { done: !!deal.tradeIn,             text: 'Trade-in captured (if any)',  action: 'lead',  cta: 'Capture' },
    { done: ['DELIVERY_BOOKED', 'RETAILED', 'DELIVERED'].includes(deal.stage), text: 'Customer committed', action: 'stage', cta: 'Update' },
    { done: !!deliveryAppt,             text: 'Delivery booked',             action: 'book',  cta: 'Book' },
    { done: deal.stage === 'DELIVERED', text: 'Vehicle delivered',           action: 'deliver', cta: 'Mark' },
  ];
}

function Row({ k, v, strong, color }) {
  return (
    <div className="flex-between" style={{ padding: '3px 0' }}>
      <span className="mono text-xs text-muted">{k}</span>
      <span
        className="mono text-xs"
        style={{ fontWeight: strong ? 700 : 400, color: color || 'var(--text-primary)' }}
      >
        {v}
      </span>
    </div>
  );
}
